import React, {useState, useEffect, useRef, useCallback} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(8, 10, 18, 0.82)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        fontFamily: '"Outfit", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
        background: 'linear-gradient(145deg, #131722, #0c0e14)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(120, 80, 212, 0.15)',
        borderRadius: '20px',
        width: '90%',
        maxWidth: '460px',
        padding: '32px',
        boxSizing: 'border-box',
        color: '#f3f4f6',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        transform: 'translateY(0)',
        transition: 'transform 0.3s ease-out'
    },
    title: {
        fontSize: '22px',
        fontWeight: '700',
        margin: 0,
        color: '#ffffff',
        background: 'linear-gradient(90deg, #a78bfa, #38bdf8)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    },
    statusText: {
        fontSize: '14px',
        color: '#9ca3af',
        margin: 0,
        lineHeight: '1.5',
        minHeight: '21px'
    },
    progressContainer: {
        margin: '8px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    progressTrack: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '99px',
        height: '12px',
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid rgba(255, 255, 255, 0.03)'
    },
    progressBar: {
        background: 'linear-gradient(90deg, #7850d4 0%, #06b6d4 100%)',
        height: '100%',
        borderRadius: '99px',
        transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 0 12px rgba(6, 182, 212, 0.5)'
    },
    percentText: {
        fontSize: '32px',
        fontWeight: '800',
        color: '#06b6d4',
        margin: 0,
        textShadow: '0 0 10px rgba(6, 182, 212, 0.2)'
    },
    terminalContainer: {
        background: '#04060a',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '14px',
        textAlign: 'left',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    terminalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px',
        color: '#4b5563',
        fontWeight: '600'
    },
    terminalTitle: {
        color: '#9ca3af'
    },
    terminalLogs: {
        height: '130px',
        overflowY: 'auto',
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: '11px',
        lineHeight: '1.6',
        color: '#10b981',
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,255,255,0.1) transparent'
    },
    toggleDetails: {
        background: 'none',
        border: 'none',
        color: '#818cf8',
        fontSize: '12px',
        cursor: 'pointer',
        padding: 0,
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        outline: 'none',
        alignSelf: 'center',
        transition: 'color 0.2s'
    },
    iconWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '10px 0'
    },
    successIcon: {
        width: '64px',
        height: '64px',
        color: '#10b981'
    },
    errorIcon: {
        width: '64px',
        height: '64px',
        color: '#ef4444'
    },
    button: {
        padding: '12px 24px',
        borderRadius: '10px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '15px',
        transition: 'all 0.2s ease',
        outline: 'none',
        marginTop: '10px'
    },
    successButton: {
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
    },
    errorButton: {
        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
        color: 'white',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
    }
};

const UploadProgressModal = ({onClose, registerUpdater}) => {
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('Initializing connection...');
    const [logs, setLogs] = useState([]);
    const [mode, setMode] = useState('loading'); // 'loading', 'success', 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const [showLogs, setShowLogs] = useState(false);

    const [isDoneHovered, setIsDoneHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);
    const [isConsoleHovered, setIsConsoleHovered] = useState(false);

    const logsEndRef = useRef(null);

    useEffect(() => {
        registerUpdater({
            setProgress,
            setStatus,
            setMode,
            setErrorMsg,
            appendLog: logLine => {
                setLogs(prev => {
                    const next = [...prev, logLine];
                    if (next.length > 500) {
                        return next.slice(next.length - 500);
                    }
                    return next;
                });
            },
            clearLogs: () => setLogs([])
        });
    }, [registerUpdater]);

    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }, [logs, showLogs]);

    const handleDoneMouseEnter = useCallback(() => setIsDoneHovered(true), []);
    const handleDoneMouseLeave = useCallback(() => setIsDoneHovered(false), []);
    const handleCloseMouseEnter = useCallback(() => setIsCloseHovered(true), []);
    const handleCloseMouseLeave = useCallback(() => setIsCloseHovered(false), []);
    const handleConsoleMouseEnter = useCallback(() => setIsConsoleHovered(true), []);
    const handleConsoleMouseLeave = useCallback(() => setIsConsoleHovered(false), []);
    const handleToggleLogs = useCallback(() => setShowLogs(prev => !prev), []);

    const doneButtonStyle = {
        ...styles.button,
        ...styles.successButton,
        transform: isDoneHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isDoneHovered ?
            '0 6px 20px rgba(16, 185, 129, 0.4)' :
            '0 4px 12px rgba(16, 185, 129, 0.25)'
    };

    const closeButtonStyle = {
        ...styles.button,
        ...styles.errorButton,
        transform: isCloseHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isCloseHovered ?
            '0 6px 20px rgba(239, 68, 68, 0.4)' :
            '0 4px 12px rgba(239, 68, 68, 0.25)'
    };

    const consoleToggleStyle = {
        ...styles.toggleDetails,
        color: isConsoleHovered ? '#a5b4fc' : '#818cf8'
    };

    const consoleArrowStyle = {
        width: '12px',
        height: '12px',
        transform: showLogs ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s'
    };

    const successTitleStyle = {
        ...styles.title,
        background: 'linear-gradient(90deg, #34d399, #059669)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    };

    const errorTitleStyle = {
        ...styles.title,
        background: 'linear-gradient(90deg, #f87171, #dc2626)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.card}>
                {mode === 'loading' && (
                    <>
                        <h2 style={styles.title}>{'Uploading Code to ESP32'}</h2>
                        <p style={styles.statusText}>{status}</p>

                        <div style={styles.progressContainer}>
                            <div style={styles.progressTrack}>
                                <div style={{...styles.progressBar, width: `${progress}%`}} />
                            </div>
                            <span style={styles.percentText}>{progress}{'%'}</span>
                        </div>
                    </>
                )}

                {mode === 'success' && (
                    <>
                        <div style={styles.iconWrapper}>
                            <svg
                                style={styles.successIcon}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <h2 style={successTitleStyle}>
                            {'Upload Successful'}
                        </h2>
                        <p style={styles.statusText}>
                            {status || 'Code burned to the ESP32 successfully.'}
                        </p>
                        <button
                            style={doneButtonStyle}
                            onClick={onClose}
                            onMouseEnter={handleDoneMouseEnter}
                            onMouseLeave={handleDoneMouseLeave}
                        >
                            {'Done'}
                        </button>
                    </>
                )}

                {mode === 'error' && (
                    <>
                        <div style={styles.iconWrapper}>
                            <svg
                                style={styles.errorIcon}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d={
                                        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 ' +
                                        '1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 ' +
                                        '16c-.77 1.333.192 3 1.732 3z'
                                    }
                                />
                            </svg>
                        </div>
                        <h2 style={errorTitleStyle}>
                            {'Upload Failed'}
                        </h2>
                        <p style={{...styles.statusText, color: '#f87171'}}>
                            {errorMsg || 'An error occurred during flashing.'}
                        </p>
                        <button
                            style={closeButtonStyle}
                            onClick={onClose}
                            onMouseEnter={handleCloseMouseEnter}
                            onMouseLeave={handleCloseMouseLeave}
                        >
                            {'Close'}
                        </button>
                    </>
                )}

                <button
                    style={consoleToggleStyle}
                    onClick={handleToggleLogs}
                    onMouseEnter={handleConsoleMouseEnter}
                    onMouseLeave={handleConsoleMouseLeave}
                >
                    {showLogs ? 'Hide Console Output' : 'Show Console Output'}
                    <svg
                        style={consoleArrowStyle}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </button>

                {showLogs && (
                    <div style={styles.terminalContainer}>
                        <div style={styles.terminalHeader}>
                            <span style={styles.terminalTitle}>{'esptool.js logger'}</span>
                            <span style={{color: '#10b981'}}>{'active'}</span>
                        </div>
                        <pre style={styles.terminalLogs}>
                            {logs.length > 0 ? logs.join('') : 'Awaiting logs...'}
                            <span ref={logsEndRef} />
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

UploadProgressModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    registerUpdater: PropTypes.func.isRequired
};

export const openUploadProgressModal = () => {
    const existing = document.getElementById('upload-progress-modal-root');
    if (existing) {
        ReactDOM.unmountComponentAtNode(existing);
        if (existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }
    }

    const modalRoot = document.createElement('div');
    modalRoot.id = 'upload-progress-modal-root';
    document.body.appendChild(modalRoot);

    let updater = null;
    const registerUpdater = u => {
        updater = u;
    };

    const handleClose = () => {
        ReactDOM.unmountComponentAtNode(modalRoot);
        if (document.body.contains(modalRoot)) {
            document.body.removeChild(modalRoot);
        }
    };

    ReactDOM.render(
        React.createElement(UploadProgressModal, {
            onClose: handleClose,
            registerUpdater: registerUpdater
        }),
        modalRoot
    );

    return {
        setProgress: p => updater?.setProgress(p),
        setStatus: s => updater?.setStatus(s),
        setMode: m => updater?.setMode(m),
        setErrorMsg: e => updater?.setErrorMsg(e),
        appendLog: logLine => updater?.appendLog(logLine),
        clearLogs: () => updater?.clearLogs(),
        close: handleClose
    };
};

export default UploadProgressModal;
